import { usePostHog } from '@posthog/react';
import { createContext, ReactNode, useContext, useEffect, useRef, useState } from 'react';
import { useStore } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

import useAuth from 'hooks/useAuth';
import { useIsMobile } from 'hooks/useIsMobile';
import { RootState, useDispatch, useSelector } from 'store';
import { portalAPI } from 'store/api/portalAPI';
import { APIUserSlice } from 'store/api/user';
import { setFloatingAiVisibility } from 'store/reducers/aiChat';
import { hideRibbon, showRibbon } from 'store/reducers/exports';
import { openDrawer } from 'store/reducers/menu';
import { closeSubDrawer } from 'store/reducers/subdrawer';
import { closeTemporaryDrawer, toggleTemporaryDrawer } from 'store/reducers/temporaryDrawer';
import { stepsFor, TOURS, TourStep } from './steps';
import TourBanner from './TourBanner';
import TourCard from './TourCard';
import { enterTourMode, leaveTourMode, TOUR_RETURN_KEY } from './tourMode';
import TourSpotlight from './TourSpotlight';

const selectCurrentUser = APIUserSlice.endpoints.getUserData.select();

/** The panels over the page that show the user's own data: the tour closes the open ones and opens them again after. */
type OpenPanels = { inbox: string; exports: boolean; ai: boolean };

/** How long a step waits for its target once its page is open, before the tour leaves it out. */
const TARGET_WAIT_MS = 2000;

/** Storage can be unavailable (privacy mode, quota): a reload during the tour then lands on real data in place. */
const withSessionStorage = (access: (storage: Storage) => void) => {
    try {
        access(sessionStorage);
    } catch {
        // Nothing to keep or clear without storage.
    }
};

export type TourSource = 'welcome' | 'help' | 'home' | 'home-offer';
type EndReason = 'completed' | 'skip' | 'exit' | 'esc';

type RunningTour = {
    /** Its key in `TOURS`, which the analytics events carry. */
    id: string;
    wording: string;
    steps: TourStep[];
    index: number;
    /** The way the user is moving, so a step left out passes them on in the same direction. */
    direction: 1 | -1;
};

type TourContextValue = {
    active: boolean;
    /** Starts the tour of that id in `TOURS`: the new design's, unless another is named. */
    start: (source: TourSource, id?: string) => Promise<void>;
    /** The `state` of the running step, from when its target is looked for until the tour moves off it or ends. */
    state?: string;
};

const TourContext = createContext<TourContextValue>({ active: false, start: async () => {} });

export const useTour = () => useContext(TourContext);

/**
 * Runs the guided tours. Starting one swaps the app onto its sample data and walks the steps the user's
 * permissions allow, each on its own page; ending it, however it ends, drops the sample data and goes back to
 * the page it started on. Every move uses `replace`, so Back afterwards never lands on a sample-data page.
 * Under 768px the sidebar is a drawer the tour keeps closed, so the steps that point into it are not part of it.
 */
export default function TourProvider({ children }: { children: ReactNode }) {
    const { user } = useAuth();
    const posthog = usePostHog();
    const dispatch = useDispatch();
    const store = useStore<Parameters<typeof selectCurrentUser>[0] & RootState>();
    const navigate = useNavigate();
    const location = useLocation();
    const isMobile = useIsMobile();
    const drawerOpen = useSelector((state) => state.menu.drawerOpen);
    const [tour, setTour] = useState<RunningTour | null>(null);
    const [found, setFound] = useState<{ step: TourStep; element: HTMLElement } | null>(null);
    const startedFrom = useRef<{ url: string; drawerOpen: boolean; panels?: OpenPanels }>({
        url: '/',
        drawerOpen: false,
    });
    const navigatedFor = useRef<TourStep | null>(null);

    const step = tour?.steps[tour.index];
    const target = found && found.step === step ? found.element : null;

    // Sample data and real data never share the cache. The current user is put straight back, so the layout,
    // which waits on it, does not wait on the server again. It is read from the store, not from the render that
    // made `start`: the Welcome's Take the tour saves seen after that render, and an older copy would reopen it.
    const resetCache = () => {
        const { data: currentUser } = selectCurrentUser(store.getState());
        dispatch(portalAPI.util.resetApiState());
        if (currentUser) {
            void dispatch(APIUserSlice.util.upsertQueryData('getUserData', undefined, currentUser));
        }
    };

    const start = async (source: TourSource, id = 'design') => {
        if (tour) return;
        const { sample, wording, steps: all } = TOURS[id];
        // Left out before the first card rather than found missing one by one: each would cost its wait, and
        // the count would shrink as the tour went.
        const steps = stepsFor(user, all).filter((candidate) => !isMobile || !candidate.target.startsWith('sidebar-'));
        const url = `${location.pathname}${location.search}${location.hash}`;
        // Under 768px the sidebar is the drawer the tour was started from: it is closed, as it would cover the
        // page, and not put back.
        startedFrom.current = { url, drawerOpen: drawerOpen && !isMobile };
        withSessionStorage((storage) => storage.setItem(TOUR_RETURN_KEY, url));
        enterTourMode(await sample(user));
        resetCache();
        // The panels over the page would show real data beside the sample, and an open Inbox would also cover the
        // steps on the right of the page. The Timecard center is closed and not put back: the layout closes it on
        // every page change, and the tour changes pages.
        const { temporaryDrawer, exports, aiChat } = store.getState();
        startedFrom.current.panels = {
            inbox: temporaryDrawer.activeDrawerId,
            exports: exports.isRibbonVisible,
            ai: aiChat.floatingVisibility === 'open',
        };
        dispatch(closeTemporaryDrawer());
        dispatch(hideRibbon());
        dispatch(setFloatingAiVisibility('closed'));
        dispatch(closeSubDrawer());
        if (isMobile) dispatch(openDrawer({ drawerOpen: false }));
        posthog.register({ tour_mode: true });
        posthog.capture('tour_started', { tour: id, source, total: steps.length });
        navigatedFor.current = null;
        setTour({ id, wording, steps, index: 0, direction: 1 });
    };

    const end = (reason: EndReason) => {
        if (!tour || !step) return;
        const total = tour.steps.length;
        if (reason === 'completed') {
            posthog.capture('tour_completed', { tour: tour.id, total });
        } else {
            posthog.capture('tour_skipped', {
                tour: tour.id,
                step: step.id,
                index: tour.index + 1,
                total,
                via: reason,
            });
        }
        posthog.unregister('tour_mode');
        setTour(null);
        setFound(null);
        leaveTourMode();
        resetCache();
        dispatch(openDrawer({ drawerOpen: startedFrom.current.drawerOpen }));
        const { panels } = startedFrom.current;
        if (panels?.inbox) dispatch(toggleTemporaryDrawer(panels.inbox));
        if (panels?.exports) dispatch(showRibbon());
        if (panels?.ai) dispatch(setFloatingAiVisibility('open'));
        withSessionStorage((storage) => storage.removeItem(TOUR_RETURN_KEY));
        navigate(startedFrom.current.url, { replace: true });
    };

    // Not while the next target is being looked for: the card still shows the step before.
    const move = (direction: 1 | -1) => {
        if (!tour || !target) return;
        const index = tour.index + direction;
        if (index >= tour.steps.length) {
            end('completed');
        } else if (index >= 0) {
            setTour({ ...tour, index, direction });
        }
    };

    // A reload ends the tour, which lives in memory only: the app goes back to where the tour started. The super
    // property PostHog keeps across loads goes too, whether the tour ended by a reload or by a closed tab.
    useEffect(() => {
        posthog.unregister('tour_mode');
        withSessionStorage((storage) => {
            const pending = storage.getItem(TOUR_RETURN_KEY);
            if (pending) {
                storage.removeItem(TOUR_RETURN_KEY);
                navigate(pending, { replace: true });
            }
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps -- once, at app start
    }, []);

    // The sidebar stays expanded while the tour points into it, even after the layout re-applies its own width.
    useEffect(() => {
        if (tour && !drawerOpen && !isMobile) {
            dispatch(openDrawer({ drawerOpen: true }));
        }
    }, [tour, drawerOpen, isMobile, dispatch]);

    // Each step opens its page, then waits for its target; one that has not rendered in time is left out.
    useEffect(() => {
        if (!tour || !step || target) return;
        if (location.pathname !== step.path && navigatedFor.current !== step) {
            navigatedFor.current = step;
            navigate(step.path, { replace: true });
            return;
        }
        const startedAt = Date.now();
        let settled = false;
        const check = () => {
            if (settled) return;
            const element = document.querySelector<HTMLElement>(`[data-tour="${step.target}"]`);
            if (element) {
                settled = true;
                // `nearest`: a target already in view scrolls nothing. Centred, it would also scroll the window itself,
                // as far as the document is taller than it, and the whole app would slide up.
                element.scrollIntoView({ block: 'nearest', inline: 'nearest' });
                setFound({ step, element });
                posthog.capture('tour_step_viewed', {
                    tour: tour.id,
                    step: step.id,
                    index: tour.index + 1,
                    total: tour.steps.length,
                });
            } else if (Date.now() - startedAt > TARGET_WAIT_MS) {
                settled = true;
                const steps = tour.steps.filter((candidate) => candidate !== step);
                if (steps.length === 0) {
                    end('completed');
                    return;
                }
                const index = tour.direction === 1 ? tour.index : tour.index - 1;
                setTour({ ...tour, steps, index: Math.min(Math.max(index, 0), steps.length - 1) });
            }
        };
        // A target already on the page is found at once, so moving between steps of one page does not flicker.
        queueMicrotask(check);
        const poll = setInterval(check, 100);
        return () => {
            settled = true;
            clearInterval(poll);
        };
        // `end` and `posthog` are read at the moment the step resolves, not tracked.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tour, step, target, location.pathname, navigate]);

    // → and Enter go on, ← goes back, Esc ends; Enter on a focused button is that button's own click. The app's own
    // shortcuts, such as ⌘K, are as inert as the page; the browser's still work.
    useEffect(() => {
        if (!tour) return;
        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                end('esc');
            } else if (event.metaKey || event.ctrlKey || event.altKey) {
                event.stopPropagation();
                return;
            } else if (!target) {
                return;
            } else if (
                event.key === 'ArrowRight' ||
                (event.key === 'Enter' && !(event.target instanceof HTMLButtonElement))
            ) {
                move(1);
            } else if (event.key === 'ArrowLeft') {
                move(-1);
            } else {
                return;
            }
            event.preventDefault();
            event.stopPropagation();
        };
        window.addEventListener('keydown', onKeyDown, true);
        return () => window.removeEventListener('keydown', onKeyDown, true);
        // eslint-disable-next-line react-hooks/exhaustive-deps -- `end` and `move` change only with `tour`
    }, [tour, target]);

    return (
        <TourContext.Provider value={{ active: tour !== null, start, state: step?.state }}>
            {children}
            {tour && (
                <>
                    {/* The card shows the last step found until the next one is, and moves on with the spotlight. */}
                    <TourSpotlight target={target}>
                        {(anchor) =>
                            found && (
                                <TourCard
                                    step={found.step}
                                    wording={tour.wording}
                                    index={tour.steps.indexOf(found.step)}
                                    total={tour.steps.length}
                                    anchor={anchor}
                                    onNext={() => move(1)}
                                    onBack={() => move(-1)}
                                    onSkip={() => end('skip')}
                                />
                            )
                        }
                    </TourSpotlight>
                    <TourBanner onExit={() => end('exit')} />
                </>
            )}
        </TourContext.Provider>
    );
}
